import { useEffect, useRef, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

export function MarkdownTextBlock({
  content,
  animate = false,
}: {
  content: string;
  animate?: boolean;
}) {
  const [displayContent, setDisplayContent] = useState(content);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!animate) {
      setDisplayContent(content);
      return;
    }

    // Typewriter assumes monotonic append. If content changes in a
    // non-append way (shrinks or diverges — e.g. structured review data
    // arrives mid-stream and strips findings sections from the text),
    // snap to the new content directly so the user does not see a
    // "clear → retype" flash. Normal append case falls through to tick().
    let shouldRunTick = true;
    setDisplayContent((prev) => {
      if (prev === content) {
        shouldRunTick = false;
        return prev;
      }
      if (!content.startsWith(prev)) {
        shouldRunTick = false;
        return content;
      }
      return prev;
    });

    if (!shouldRunTick) {
      return;
    }

    const tick = () => {
      setDisplayContent((prev) => {
        if (prev === content) {
          timerRef.current = null;
          return prev;
        }

        const nextLength = Math.min(content.length, prev.length + Math.max(1, Math.ceil((content.length - prev.length) / 12)));
        const nextValue = content.slice(0, nextLength);

        if (nextValue !== content) {
          timerRef.current = window.setTimeout(tick, 24);
        } else {
          timerRef.current = null;
        }

        return nextValue;
      });
    };

    timerRef.current = window.setTimeout(tick, 24);

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, [animate, content]);

  return (
    <div className="prose prose-sm max-w-none text-[var(--color-fg)] prose-headings:text-[var(--color-fg)] prose-p:text-[var(--color-fg)] prose-strong:text-[var(--color-fg)] prose-code:text-[var(--color-fg)] prose-pre:bg-[var(--color-surface)] prose-pre:text-[var(--color-fg)] prose-blockquote:text-[var(--color-fg-secondary)] prose-li:text-[var(--color-fg)] prose-th:text-[var(--color-fg-secondary)] prose-td:text-[var(--color-fg)]">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="mt-5 mb-2 text-lg font-semibold leading-snug" style={{ fontFamily: "var(--font-display)" }}>
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="mt-4 mb-2 text-base font-semibold leading-snug" style={{ fontFamily: "var(--font-display)" }}>
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="mt-3 mb-1.5 text-sm font-semibold leading-snug" style={{ fontFamily: "var(--font-display)" }}>
              {children}
            </h3>
          ),
          p: ({ children }) => <p className="mb-3 text-sm leading-relaxed last:mb-0">{children}</p>,
          ul: ({ children }) => <ul className="mb-3 list-disc pl-5 text-sm leading-relaxed last:mb-0">{children}</ul>,
          ol: ({ children }) => <ol className="mb-3 list-decimal pl-5 text-sm leading-relaxed last:mb-0">{children}</ol>,
          li: ({ children }) => <li className="mb-1">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="my-3 border-l-2 border-[var(--color-border)] pl-4 text-[var(--color-fg-secondary)]">
              {children}
            </blockquote>
          ),
          code: ({ className, children }) =>
            className ? (
              <code className={className}>{children}</code>
            ) : (
              <code className="rounded bg-[var(--color-surface)] px-1.5 py-0.5 text-[12px]">{children}</code>
            ),
          pre: ({ children }) => (
            <pre className="my-3 overflow-x-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-[12px] leading-relaxed">
              {children}
            </pre>
          ),
          table: ({ children }) => (
            <div className="my-3 w-full overflow-x-auto rounded-lg border border-[var(--color-border)]">
              <table className="w-full border-collapse text-sm">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-[var(--color-border)]/30">{children}</thead>,
          th: ({ children }) => (
            <th
              className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider border-b border-[var(--color-border)]"
              style={{ fontFamily: "var(--font-mono)" }}
            >
              {children}
            </th>
          ),
          td: ({ children }) => <td className="px-3 py-2 align-top border-b border-[var(--color-border)]/70">{children}</td>,
          hr: () => <hr className="my-4 border-[var(--color-border)]" />,
        }}
      >
        {displayContent}
      </Markdown>
    </div>
  );
}
